/**
 * The web build's update mechanics (updates.ts wires them to the browser): when to look for a new deploy, and how to
 * move a tab onto it. Kept free of globals so they can be unit-tested.
 *
 * Why it looks like this: page loads are network-first in the service worker (sw/sw.ts), so a reload always fetches
 * the live index.html. A new worker still waits ("prompt" mode, so an open tab keeps the lazy chunks it precached);
 * applying an update activates it when there is one and reloads either way.
 */

/** A periodic look at /version.json and the worker script (Slack and Meet poll a few times an hour). */
export const CHECK_EVERY_MS = 15 * 60 * 1000;
/** Focus, visibility and online events come in bursts: at most one check per minute from them. */
export const MIN_CHECK_GAP_MS = 60 * 1000;
/** How long to wait for the new worker to take control before reloading anyway. */
export const ACTIVATE_TIMEOUT_MS = 3000;

/** Check now? `force` (the timer, "Check for updates") ignores the gap; events don't. */
export function shouldCheck(lastCheckAt: number | undefined, now: number, force = false): boolean {
  return force || lastCheckAt === undefined || now - lastCheckAt >= MIN_CHECK_GAP_MS;
}

/** The slice of ServiceWorkerRegistration / ServiceWorkerContainer this needs. */
export type SwRegistration = { waiting: { postMessage(message: unknown): void } | null };
export type SwContainer = { addEventListener(type: "controllerchange", listener: () => void, options?: { once?: boolean }): void };

export type ApplyDeps = {
  registration: SwRegistration | undefined;
  container: SwContainer | undefined;
  reload: () => void;
  setTimeout: (fn: () => void, ms: number) => unknown;
};

/**
 * Reload into the newest build. A waiting worker is told to SKIP_WAITING first and the reload follows its takeover
 * (or a short timeout, so a stuck worker never leaves the button dead); with no waiting worker the reload alone is
 * enough, since navigations go to the network first.
 */
export function applyWebUpdate({ registration, container, reload, setTimeout }: ApplyDeps): "activate" | "reload" {
  const waiting = registration?.waiting ?? null;
  if (waiting === null || container === undefined) {
    reload();
    return "reload";
  }
  let done = false;
  const once = () => {
    if (done) return;
    done = true;
    reload();
  };
  container.addEventListener("controllerchange", once, { once: true });
  setTimeout(once, ACTIVATE_TIMEOUT_MS);
  waiting.postMessage({ type: "SKIP_WAITING" });
  return "activate";
}
